import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Plus, Trash, CircleNotch, Repeat, Copy, ArrowSquareOut, Power, Users, ChartLineUp, Gift } from "@phosphor-icons/react";
import { RowanTerm } from "@/components/RowanTerm";

const INTERVALS = [
  { key: "weekly", label: "per week" },
  { key: "monthly", label: "per month" },
  { key: "yearly", label: "per year" },
];
const EMPTY = { name: "", description: "", amount: "", interval: "monthly", perk_type: "none", perk_value: "", perk_label: "" };

export function Memberships({ businessId }) {
  const [items, setItems] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(EMPTY);

  const load = () => api.get(`/businesses/${businessId}/memberships`).then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [businessId]);

  const create = async () => {
    if (!form.name.trim()) return toast.error("Name your membership");
    if (!(parseFloat(form.amount) > 0)) return toast.error("Enter a price greater than zero");
    setSaving(true);
    try {
      await api.post(`/businesses/${businessId}/memberships`, {
        name: form.name.trim(), description: form.description.trim(),
        amount: parseFloat(form.amount), interval: form.interval, currency: "usd",
        perk_type: form.perk_type,
        perk_value: parseFloat(form.perk_value) || 0,
        perk_label: form.perk_label.trim(),
      });
      toast.success("Membership offer created — share the link!");
      setForm(EMPTY);
      setShowForm(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't create membership.");
    } finally { setSaving(false); }
  };

  const toggle = async (m) => {
    try {
      const { data } = await api.patch(`/memberships/${m.id}`, { active: !m.active });
      setItems((xs) => xs.map((x) => (x.id === m.id ? data : x)));
    } catch { toast.error("Couldn't update."); }
  };

  const del = async (id) => {
    try { await api.delete(`/memberships/${id}`); setItems((x) => x.filter((m) => m.id !== id)); toast.success("Membership removed"); } catch {}
  };

  const copyLink = (url) => { navigator.clipboard?.writeText(url)?.catch(() => {}); toast.success("Payment link copied"); };

  const redeem = async (m) => {
    try {
      const { data } = await api.post(`/memberships/${m.id}/redeem`);
      if (data.kind === "discount") navigator.clipboard?.writeText(data.code)?.catch(() => {});
      toast.success(data.message);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't apply the perk.");
    }
  };

  const perkText = (m) => {
    if (m.perk_type === "booking") return m.perk_label || "Free booking credit";
    if (m.perk_type === "discount") return m.perk_label || `${Number(m.perk_value) || 0}% member discount`;
    return "";
  };

  const totalSubs = items.reduce((s, m) => s + (m.active_subscribers || 0), 0);
  const totalMrr = items.reduce((s, m) => s + (m.mrr || 0), 0);

  return (
    <div className="space-y-6" data-testid="memberships-panel">
      <div className="bk-card p-6 flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-start gap-3">
          <Repeat size={24} weight="duotone" color="var(--bk-accent)" className="shrink-0 mt-0.5" />
          <div>
            <h3 className="font-display text-xl font-bold" style={{ color: "var(--bk-text)" }}><RowanTerm term="recurring_revenue">Memberships</RowanTerm></h3>
            <p className="text-sm mt-1" style={{ color: "var(--bk-text-2)" }}>
              Create a subscription offer and share its link — clients get billed automatically on repeat, powered by Stripe.
            </p>
          </div>
        </div>
        <button onClick={() => setShowForm((s) => !s)} data-testid="new-membership-btn" className="bk-btn-primary inline-flex items-center gap-2 px-4 py-2.5 rounded-full text-sm font-semibold shrink-0">
          <Plus size={16} weight="bold" /> New offer
        </button>
      </div>

      {items.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4" data-testid="membership-stats">
          <div className="rounded-2xl p-5 text-white" style={{ background: "var(--bk-gradient-primary)" }}>
            <p className="text-xs font-semibold uppercase tracking-wide" style={{ color: "rgba(255,255,255,0.8)" }}>Recurring revenue (MRR)</p>
            <p className="font-mono text-3xl font-bold mt-1" data-testid="stat-mrr">${totalMrr.toFixed(2)}</p>
            <p className="text-xs mt-1" style={{ color: "rgba(255,255,255,0.8)" }}>≈ ${(totalMrr * 12).toFixed(0)}/yr at this pace</p>
          </div>
          <div className="bk-card p-5">
            <p className="text-xs font-semibold uppercase tracking-wide" style={{ color: "var(--bk-text-2)" }}>Active members</p>
            <p className="font-mono text-3xl font-bold mt-1" style={{ color: "var(--bk-text)" }} data-testid="stat-subs">{totalSubs}</p>
            <p className="text-xs mt-1" style={{ color: "var(--bk-text-2)" }}>across {items.length} offer{items.length === 1 ? "" : "s"}</p>
          </div>
          <div className="bk-card p-5">
            <p className="text-xs font-semibold uppercase tracking-wide" style={{ color: "var(--bk-text-2)" }}>Live offers</p>
            <p className="font-mono text-3xl font-bold mt-1" style={{ color: "var(--bk-text)" }}>{items.filter((m) => m.active).length}</p>
            <p className="text-xs mt-1" style={{ color: "var(--bk-text-2)" }}>{items.filter((m) => !m.active).length} paused</p>
          </div>
        </div>
      )}

      {showForm && (
        <div className="bk-card p-6 space-y-3" data-testid="membership-form">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <input placeholder="Offer name (e.g. Monthly Wellness Membership)" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} data-testid="membership-name" className="rounded-md border px-3 py-2 text-sm outline-none sm:col-span-2" style={{ borderColor: "var(--bk-border)" }} />
            <input type="number" min="0" step="0.01" placeholder="Price (USD)" value={form.amount} onChange={(e) => setForm((f) => ({ ...f, amount: e.target.value }))} data-testid="membership-amount" className="rounded-md border px-3 py-2 text-sm outline-none" style={{ borderColor: "var(--bk-border)" }} />
            <select value={form.interval} onChange={(e) => setForm((f) => ({ ...f, interval: e.target.value }))} data-testid="membership-interval" className="rounded-md border px-3 py-2 text-sm bg-white outline-none" style={{ borderColor: "var(--bk-border)" }}>
              {INTERVALS.map((i) => <option key={i.key} value={i.key}>{i.label}</option>)}
            </select>
            <input placeholder="Short description (optional)" value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} data-testid="membership-description" className="rounded-md border px-3 py-2 text-sm outline-none sm:col-span-2" style={{ borderColor: "var(--bk-border)" }} />
            <div className="sm:col-span-2 rounded-md border p-3" style={{ borderColor: "var(--bk-border)", background: "var(--bk-page)" }}>
              <p className="text-xs font-semibold uppercase tracking-wide mb-2 flex items-center gap-1.5" style={{ color: "var(--bk-text-2)" }}><Gift size={14} weight="duotone" /> Member perk (auto-unlock)</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <select value={form.perk_type} onChange={(e) => setForm((f) => ({ ...f, perk_type: e.target.value }))} data-testid="membership-perk-type" className="rounded-md border px-3 py-2 text-sm bg-white outline-none" style={{ borderColor: "var(--bk-border)" }}>
                  <option value="none">No perk</option>
                  <option value="booking">Free booking credit</option>
                  <option value="discount">Discount</option>
                </select>
                {form.perk_type === "discount" && (
                  <input type="number" min="0" placeholder="Discount %" value={form.perk_value} onChange={(e) => setForm((f) => ({ ...f, perk_value: e.target.value }))} data-testid="membership-perk-value" className="rounded-md border px-3 py-2 text-sm outline-none" style={{ borderColor: "var(--bk-border)" }} />
                )}
                {form.perk_type !== "none" && (
                  <input placeholder={form.perk_type === "booking" ? "Booking label (e.g. Free monthly massage)" : "Perk label (optional)"} value={form.perk_label} onChange={(e) => setForm((f) => ({ ...f, perk_label: e.target.value }))} data-testid="membership-perk-label" className="rounded-md border px-3 py-2 text-sm outline-none sm:col-span-2" style={{ borderColor: "var(--bk-border)" }} />
                )}
              </div>
            </div>
          </div>
          <div className="flex justify-end">
            <button onClick={create} disabled={saving} data-testid="save-membership-btn" className="bk-btn-primary inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold disabled:opacity-70">
              {saving ? <><CircleNotch size={16} className="animate-spin" /> Creating…</> : <>Create offer</>}
            </button>
          </div>
        </div>
      )}

      {items.length === 0 ? (
        <div className="bk-card p-10 text-center">
          <Repeat size={40} weight="duotone" color="var(--bk-accent)" className="mx-auto" />
          <p className="font-semibold mt-3" style={{ color: "var(--bk-text)" }}>No membership offers yet</p>
          <p className="text-sm mt-1" style={{ color: "var(--bk-text-2)" }}>Create a recurring offer (e.g. a $99/mo membership) and share the pay-link with your clients.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {items.map((m) => {
            const intLabel = INTERVALS.find((i) => i.key === m.interval)?.label || m.interval;
            return (
              <div key={m.id} data-testid={`membership-${m.id}`} className="bk-card p-5" style={{ opacity: m.active ? 1 : 0.6 }}>
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="font-display text-lg font-bold truncate" style={{ color: "var(--bk-text)" }}>{m.name}</p>
                    {m.description && <p className="text-sm mt-0.5" style={{ color: "var(--bk-text-2)" }}>{m.description}</p>}
                  </div>
                  <span className="text-xs font-bold px-2.5 py-1 rounded-full shrink-0" style={{ background: m.active ? "var(--bk-success-bg)" : "var(--bk-border)", color: m.active ? "var(--bk-success)" : "var(--bk-text-2)" }}>
                    {m.active ? "Active" : "Paused"}
                  </span>
                </div>
                <p className="font-mono text-2xl font-bold mt-3" style={{ color: "var(--bk-accent)" }}>
                  ${Number(m.amount).toFixed(2)} <span className="text-sm font-medium" style={{ color: "var(--bk-text-2)" }}>{intLabel}</span>
                </p>
                <div className="flex items-center gap-3 mt-2 text-sm" data-testid={`membership-subs-${m.id}`} style={{ color: "var(--bk-text-2)" }}>
                  <span className="inline-flex items-center gap-1.5"><Users size={15} weight="duotone" /> <strong style={{ color: "var(--bk-text)" }}>{m.active_subscribers || 0}</strong> active</span>
                  <span className="inline-flex items-center gap-1.5"><ChartLineUp size={15} weight="duotone" /> <strong style={{ color: "var(--bk-text)" }}>${Number(m.mrr || 0).toFixed(2)}</strong>/mo</span>
                </div>
                {m.perk_type && m.perk_type !== "none" && (
                  <div className="flex items-center justify-between gap-2 mt-3 rounded-lg px-3 py-2" style={{ background: "var(--bk-accent-glow)" }} data-testid={`membership-perk-${m.id}`}>
                    <span className="inline-flex items-center gap-1.5 text-sm font-semibold" style={{ color: "var(--bk-accent)" }}>
                      <Gift size={16} weight="fill" /> {perkText(m)}
                    </span>
                    <button onClick={() => redeem(m)} data-testid={`membership-redeem-${m.id}`} className="text-xs font-bold px-3 py-1.5 rounded-full text-white shrink-0" style={{ background: "var(--bk-accent)" }}>
                      {m.perk_type === "booking" ? "Unlock booking" : "Get code"}
                    </button>
                  </div>
                )}
                <div className="flex flex-wrap items-center gap-2 mt-4">
                  <button onClick={() => copyLink(m.payment_link_url)} data-testid={`membership-copy-${m.id}`} className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-full border hover:bg-slate-50" style={{ borderColor: "var(--bk-border)", color: "var(--bk-text)" }}>
                    <Copy size={14} weight="bold" /> Copy link
                  </button>
                  <a href={m.payment_link_url} target="_blank" rel="noreferrer" data-testid={`membership-open-${m.id}`} className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-full border hover:bg-slate-50" style={{ borderColor: "var(--bk-border)", color: "var(--bk-text)" }}>
                    <ArrowSquareOut size={14} weight="bold" /> Open
                  </a>
                  <button onClick={() => toggle(m)} data-testid={`membership-toggle-${m.id}`} className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-full border hover:bg-slate-50" style={{ borderColor: "var(--bk-border)", color: "var(--bk-text-2)" }}>
                    <Power size={14} weight="bold" /> {m.active ? "Pause" : "Resume"}
                  </button>
                  <button onClick={() => del(m.id)} data-testid={`membership-delete-${m.id}`} className="p-2 rounded-full hover:bg-black/5 ml-auto" style={{ color: "var(--bk-energy)" }} aria-label="Delete"><Trash size={15} weight="bold" /></button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
